import { useState, useEffect } from "react";
import { apiClient } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { 
  Plus, 
  Search, 
  MoreVertical, 
  Edit, 
  Trash2, 
  ArrowRight,
  Building2,
  Mail,
  Phone,
  MapPin,
  Users
} from "lucide-react";

const statusConfig = {
  lead: { label: "Lead", className: "bg-zinc-800 text-zinc-300 border-zinc-700" },
  prospect: { label: "Prospect", className: "bg-blue-900/50 text-blue-300 border-blue-800" },
  client: { label: "Client", className: "bg-emerald-900/50 text-emerald-300 border-emerald-800" },
};

const emptyClient = {
  nom: "",
  entreprise: "",
  email: "",
  telephone: "",
  adresse: "",
  statut: "lead",
  notes: "",
};

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState(emptyClient);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await apiClient.get("/clients");
      setClients(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des clients");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await apiClient.put(`/clients/${editingClient.id}`, formData);
        toast.success("Client mis à jour");
      } else {
        await apiClient.post("/clients", formData);
        toast.success("Client créé");
      }
      setDialogOpen(false);
      setEditingClient(null);
      setFormData(emptyClient);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      nom: client.nom,
      entreprise: client.entreprise,
      email: client.email || "",
      telephone: client.telephone || "",
      adresse: client.adresse || "",
      statut: client.statut,
      notes: client.notes || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce client ?")) return;
    try {
      await apiClient.delete(`/clients/${id}`);
      toast.success("Client supprimé");
      fetchClients();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleConvert = async (id, newStatus) => {
    try {
      await apiClient.post(`/clients/${id}/convertir`, { statut: newStatus });
      toast.success(`Client converti en ${statusConfig[newStatus].label}`);
      fetchClients();
    } catch (error) {
      toast.error("Erreur lors de la conversion");
    }
  };

  const filteredClients = clients.filter((client) => {
    const matchesSearch = 
      client.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.entreprise.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === "all" || client.statut === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getNextStatus = (currentStatus) => {
    if (currentStatus === "lead") return "prospect";
    if (currentStatus === "prospect") return "client";
    return null;
  };

  return (
    <div className="space-y-6" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-heading font-bold text-white tracking-tight">Clients</h1>
          <p className="text-zinc-500 mt-1">Gérez vos leads, prospects et clients</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setEditingClient(null);
            setFormData(emptyClient);
          }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700 glow-primary" data-testid="add-client-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau client
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#121214] border-zinc-800 max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-white">
                {editingClient ? "Modifier le client" : "Nouveau client"}
              </DialogTitle>
              <DialogDescription className="text-zinc-500">
                {editingClient ? "Modifiez les informations du client" : "Ajoutez un nouveau lead, prospect ou client"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400">Nom *</Label>
                  <Input
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    required
                    data-testid="client-nom-input"
                    className="bg-zinc-900/50 border-zinc-800"
                    placeholder="Jean Dupont"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400">Entreprise *</Label>
                  <Input
                    value={formData.entreprise}
                    onChange={(e) => setFormData({ ...formData, entreprise: e.target.value })}
                    required
                    data-testid="client-entreprise-input"
                    className="bg-zinc-900/50 border-zinc-800"
                    placeholder="Société ABC"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400">Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    data-testid="client-email-input"
                    className="bg-zinc-900/50 border-zinc-800"
                    placeholder="email@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400">Téléphone</Label>
                  <Input
                    value={formData.telephone}
                    onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                    data-testid="client-telephone-input"
                    className="bg-zinc-900/50 border-zinc-800"
                    placeholder="+33 1 23 45 67 89"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">Adresse</Label>
                <Input
                  value={formData.adresse}
                  onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
                  data-testid="client-adresse-input"
                  className="bg-zinc-900/50 border-zinc-800"
                  placeholder="123 Rue Example, 75001 Paris"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">Statut</Label>
                <Select
                  value={formData.statut}
                  onValueChange={(value) => setFormData({ ...formData, statut: value })}
                >
                  <SelectTrigger className="bg-zinc-900/50 border-zinc-800" data-testid="client-statut-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121214] border-zinc-800">
                    <SelectItem value="lead">Lead</SelectItem>
                    <SelectItem value="prospect">Prospect</SelectItem>
                    <SelectItem value="client">Client</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  data-testid="client-notes-input"
                  className="bg-zinc-900/50 border-zinc-800 min-h-[80px]"
                  placeholder="Notes additionnelles..."
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="border-zinc-700 text-zinc-300"
                >
                  Annuler
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="client-submit-btn">
                  {editingClient ? "Mettre à jour" : "Créer"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            placeholder="Rechercher un client..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-zinc-900/50 border-zinc-800"
            data-testid="clients-search-input"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px] bg-zinc-900/50 border-zinc-800" data-testid="clients-filter-select">
            <SelectValue placeholder="Filtrer par statut" />
          </SelectTrigger>
          <SelectContent className="bg-[#121214] border-zinc-800">
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="lead">Leads</SelectItem>
            <SelectItem value="prospect">Prospects</SelectItem>
            <SelectItem value="client">Clients</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {Object.entries(statusConfig).map(([status, config]) => {
          const count = clients.filter(c => c.statut === status).length;
          return (
            <Card key={status} className="bg-[#121214] border-zinc-800">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-widest text-zinc-500">{config.label}s</p>
                  <p className="text-2xl font-heading font-bold text-white">{count}</p>
                </div>
                <Badge variant="outline" className={config.className}>{config.label}</Badge>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Clients List */}
      <div className="grid gap-4">
        {loading ? (
          <div className="text-center py-12 text-zinc-500">Chargement...</div>
        ) : filteredClients.length === 0 ? (
          <Card className="bg-[#121214] border-zinc-800">
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 mx-auto text-zinc-700 mb-4" />
              <p className="text-zinc-500">Aucun client trouvé</p>
              <p className="text-zinc-600 text-sm mt-1">Ajoutez votre premier client pour commencer</p>
            </CardContent>
          </Card>
        ) : (
          filteredClients.map((client) => (
            <Card key={client.id} className="bg-[#121214] border-zinc-800 card-hover" data-testid={`client-card-${client.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-heading font-semibold text-white truncate">{client.nom}</h3>
                      <Badge variant="outline" className={statusConfig[client.statut].className}>
                        {statusConfig[client.statut].label}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 text-sm">
                      <div className="flex items-center gap-2 text-zinc-400">
                        <Building2 className="w-4 h-4 text-zinc-600" />
                        <span className="truncate">{client.entreprise}</span>
                      </div>
                      {client.email && (
                        <div className="flex items-center gap-2 text-zinc-400">
                          <Mail className="w-4 h-4 text-zinc-600" />
                          <span className="truncate">{client.email}</span>
                        </div>
                      )}
                      {client.telephone && (
                        <div className="flex items-center gap-2 text-zinc-400">
                          <Phone className="w-4 h-4 text-zinc-600" />
                          <span className="font-mono">{client.telephone}</span>
                        </div>
                      )}
                      {client.adresse && (
                        <div className="flex items-center gap-2 text-zinc-400">
                          <MapPin className="w-4 h-4 text-zinc-600" />
                          <span className="truncate">{client.adresse}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="text-zinc-400 hover:text-white" data-testid={`client-menu-${client.id}`}>
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-[#121214] border-zinc-800">
                      <DropdownMenuItem onClick={() => handleEdit(client)} className="text-zinc-300 focus:bg-zinc-800">
                        <Edit className="w-4 h-4 mr-2" />
                        Modifier
                      </DropdownMenuItem>
                      {getNextStatus(client.statut) && (
                        <DropdownMenuItem 
                          onClick={() => handleConvert(client.id, getNextStatus(client.statut))}
                          className="text-blue-400 focus:bg-zinc-800"
                        >
                          <ArrowRight className="w-4 h-4 mr-2" />
                          Convertir en {statusConfig[getNextStatus(client.statut)].label}
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator className="bg-zinc-800" />
                      <DropdownMenuItem 
                        onClick={() => handleDelete(client.id)} 
                        className="text-red-400 focus:bg-zinc-800"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Supprimer
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
